/**
 * PRP-WEB-1 W2.5: one estimate on the project Overview: name, where it came from, its total (or
 * "not priced yet"), rooms, lines, when it last changed, a primary Open (one click to the line
 * items) and a ⋯ menu with Delete.
 */
import { useEffect, useRef, useState } from 'react';

import type { EstimateSource, OverviewEstimate } from '../../lib/projectOverviewApi';
import { formatRelativeTime } from '../../lib/utils';
import { fmtMoney } from '../lib/format';
import { IconMore, IconTrash } from './icons';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Pill } from './ui/pill';

/**
 * The project's name in the breadcrumb ("Estimates / <this>"): the project page and the floor plan
 * page use this one function so the two crumbs always read the same.
 */
export function projectLabel(project?: { owner_name?: string | null } | null): string {
  return project?.owner_name || 'Estimate';
}

const SOURCE_LABEL: Record<EstimateSource, string> = { chat: 'Chat', import: 'Imported' };
const SOURCE_KIND = { chat: 'accent', import: 'neutral' } as const;

/** "changed 2 hours ago", or '' when the time is unknown or unreadable. */
export function changedAgo(iso: string | null | undefined): string {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return '';
  return `changed ${formatRelativeTime(iso)}`;
}

/** The exact local time, for a hover title. */
export function exactTime(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleString();
}

export interface EstimateCardProps {
  estimate: OverviewEstimate;
  /** True when the card came from the imports list, not the overview (no totals or counts known). */
  partial?: boolean;
  onOpen: () => void;
  onDelete: () => void;
}

export default function EstimateCard({ estimate: e, partial, onOpen, onDelete }: EstimateCardProps) {
  const [menu, setMenu] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const onDown = (ev: MouseEvent) => {
      if (box.current && !box.current.contains(ev.target as Node)) setMenu(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const ago = changedAgo(e.last_changed_at);
  const facts = partial
    ? []
    : [`${e.rooms} room${e.rooms === 1 ? '' : 's'}`, `${e.line_items} line${e.line_items === 1 ? '' : 's'}`];

  return (
    <Card className="flex flex-wrap items-center gap-3 p-3.5" data-testid={`estimate-card-${e.parse_hash}`}>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[14px] font-[580] text-[var(--tx-hi)]">{e.name}</span>
          <Pill kind={SOURCE_KIND[e.source]}>{SOURCE_LABEL[e.source]}</Pill>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--tx-lo)]">
          {!partial && (
            <span
              className="font-[var(--font-mono)] tabular-nums text-[var(--tx-mid)]"
              data-testid="estimate-total"
            >
              {e.total != null ? fmtMoney(e.total, true) : 'not priced yet'}
            </span>
          )}
          {facts.map((f) => (
            <span key={f}>{f}</span>
          ))}
          {ago && <span title={exactTime(e.last_changed_at)}>{ago}</span>}
          {e.follows_plan && (
            <span className="text-[var(--tx-faint)]" data-testid="follows-plan">
              follows the floor plan
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button variant="primary" size="sm" onClick={onOpen} data-testid="estimate-open">
          Open
        </Button>
        <div className="relative" ref={box}>
          <Button
            variant="icon"
            size="sm"
            aria-label={`More for ${e.name}`}
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => setMenu((m) => !m)}
          >
            <IconMore />
          </Button>
          {menu && (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+4px)] z-30 w-[160px] rounded-[var(--r-lg)] border border-[var(--bd)] bg-[var(--bg-2)] p-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  onDelete();
                }}
                className="flex h-[30px] w-full items-center gap-2 rounded-[var(--r-sm)] px-2 text-left text-[12.5px] text-[var(--bad)] hover:bg-[var(--bg-3)]"
              >
                <IconTrash className="h-[14px] w-[14px]" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
